import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { RM } from '../lib/rm';
import type { Attachment } from '../lib/attachments';
import { RevealingContent } from './RevealingContent';

// User bubble: lifts from the composer field (FLIP) and its chars settle in.
export function UserMessage({
  content,
  attachments,
  flipRect,
}: {
  content: string;
  attachments?: Attachment[];
  flipRect: DOMRect | null;
}) {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const ran = useRef(false);

  useLayoutEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const bubble = bubbleRef.current;
    if (!bubble) return; // image-only message: no text bubble to animate

    if (RM || !flipRect) {
      bubble.textContent = content;
      return;
    }

    bubble.textContent = content;
    const last = bubble.getBoundingClientRect();
    const dx = flipRect.left + 16 - last.left;
    const dy = flipRect.top + 12 - last.top;
    gsap.fromTo(bubble, { x: dx, y: dy, opacity: 0.4 }, { x: 0, y: 0, opacity: 1, duration: 0.32, ease: 'expo.out' });

    // Per-character settle is a nice touch for a short message but scales
    // badly for a long paste — one DOM node + one tween per character, with a
    // fixed stagger, stretches the reveal out over many seconds and *reads*
    // as a hang even though nothing is actually stuck. Above this length just
    // let the whole-bubble fade above carry it.
    if (content.length > 240) return;

    bubble.textContent = '';
    // Wrap each word's glyphs in a `.w` (nowrap) so words never break mid-word;
    // whitespace stays as real (breakable) text nodes.
    const chars: HTMLSpanElement[] = [];
    for (const part of content.split(/(\s+)/)) {
      if (part === '') continue;
      if (/^\s+$/.test(part)) {
        bubble.appendChild(document.createTextNode(part));
        continue;
      }
      const w = document.createElement('span');
      w.className = 'w';
      for (const ch of part) {
        const s = document.createElement('span');
        s.className = 'ch';
        s.textContent = ch;
        w.appendChild(s);
        chars.push(s);
      }
      bubble.appendChild(w);
    }
    gsap.from(chars, {
      opacity: 0,
      y: () => gsap.utils.random(-6, 10),
      duration: 0.22,
      // Cap the total spread regardless of length instead of a fixed per-char
      // gap, so a longer (but still under the cutoff above) message doesn't
      // creep toward the same slow-crawl problem.
      stagger: { each: Math.min(0.004, 0.18 / chars.length), from: 'start' },
      ease: 'power2.out',
    });
  }, []);

  return (
    <div className="msg you">
      {attachments && attachments.length > 0 && (
        <div className="msg-images">
          {attachments.map((a, i) => (
            <img key={i} src={a.url} alt={a.name || 'attached image'} />
          ))}
        </div>
      )}
      {content.trim() !== '' && <div className="bubble" ref={bubbleRef} />}
    </div>
  );
}

// Twin message: the bubble shell around RevealingContent (see that file for
// the per-character reveal / code-toolbar engine, shared with the /aui
// surface's assistant text-part renderer).
export function TwinMessage(props: {
  content: string;
  streaming: boolean;
  searching?: boolean;
  instant?: boolean;
  onLanded?: () => void;
}) {
  return (
    <div className="msg twin">
      <div className="bubble">
        <RevealingContent {...props} />
      </div>
    </div>
  );
}
